import globalLogger from "@lazarentertainment/js-logger"
import { captureException } from "@sentry/nextjs"
import { Component, ReactNode } from "react"

import ErrorMessage from "./ErrorMessage"

interface Props {
  fallback?: ReactNode
  children?: ReactNode
}

interface State {
  hasError: boolean
}

// Catches errors in children during render - taken from Nextjs docs
// https://nextjs.org/docs/advanced-features/error-handling#handling-client-errors
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: any) {
    globalLogger.info(error)
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  // https://reactjs.org/docs/react-component.html#componentdidcatch
  componentDidCatch(error: any, errorInfo: any) {
    // `errorInfo` is react component stack info that's not part of the actual js callstack
    captureException({ error, errorInfo })
    globalLogger.error(error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorMessage />
    }

    return this.props.children
  }
}

export { ErrorBoundary }
